import * as Sentry from '@sentry/browser'
import axios from 'axios'
import { API_BASE_URL, X_API_KEY } from './api'

interface ImageUploadResponse {
  files: {
    fileName: string
    size: number
    status: number
  }[]
}

export const fetchImages = async (userId: string, token: string) => {
  try {
    const response = await fetch(`${API_BASE_URL}/user/${userId}/gallery`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch images: ${response.statusText}`)
    }

    const data = await response.json()

    if (!Array.isArray(data)) {
      throw new Error('Fetched data is not an array')
    }

    return data.map(image => ({
      ...image,
      url: `${API_BASE_URL}/images/${userId}/${image.fileName}`,
    }))
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }
}

export const fetchGallery = async (userId: number) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/${userId}/gallery`, {
      headers: { 'x-api-key': X_API_KEY },
    })

    return response.data
  } catch (error) {
    console.error('Failed to fetch gallery images:', error)
    return []
  }
}

export const uploadImages = async (
  userId: string,
  token: string,
  files: File[],
) => {
  const formData = new FormData()
  const commonData = {
    userId: userId.toString(),
    cover: 'false',
    status: '0',
  }

  Object.entries(commonData).forEach(([key, value]) => {
    formData.append(key, value)
  })

  files.forEach((file, index) => {
    const fileName = `image-${userId}-${Date.now()}-${index}`
    const newFile = new File([file], fileName, { type: file.type })
    formData.append(`media`, newFile)
    formData.append(`fileName`, fileName)
    formData.append(`size`, file.size.toString())
  })

  try {
    const response = await axios.post<ImageUploadResponse>(
      `${API_BASE_URL}/user/${userId}/gallery`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
          'x-api-key': X_API_KEY,
        },
      },
    )

    return response.data.files.map(file => ({
      userId,
      fileName: file.fileName,
      cover: false,
      size: file.size,
      status: 0,
      url: `${API_BASE_URL}/images/${userId}/${file.fileName}`,
    }))
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }
}

export const uploadVerificationImage = async (
  userId: string,
  token: string,
  files: File[],
) => {
  const formData = new FormData()
  const commonData = {
    userId: userId.toString(),
    cover: 'false',
    status: '4',
  }

  Object.entries(commonData).forEach(([key, value]) => {
    formData.append(key, value)
  })

  files.forEach((file, index) => {
    const fileName = `(verification-${userId})${Date.now()}-${index}.${file.name
      .split('.')
      .pop()}`
    const newFile = new File([file], fileName, { type: file.type })
    formData.append('media', newFile)
    formData.append('fileName', fileName)
    formData.append('size', file.size.toString())
  })

  try {
    const response = await axios.post<ImageUploadResponse>(
      `${API_BASE_URL}/user/${userId}/gallery`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
          'x-api-key': X_API_KEY,
        },
      },
    )

    return response.data.files.map(file => ({
      userId,
      fileName: file.fileName,
      cover: false,
      size: file.size,
      status: 4,
      url: `${API_BASE_URL}/images/${userId}/${file.fileName}`,
    }))
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }
}

export const updateImage = async (
  userId: string,
  token: string,
  fileName: string,
  newUrl: string,
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/user/${userId}/gallery/${encodeURIComponent(fileName)}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-api-key': X_API_KEY,
        },
        body: JSON.stringify({ url: newUrl }),
      },
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(
        `Failed to update image: ${response.status} ${
          response.statusText
        }. ${JSON.stringify(errorData)}`,
      )
    }

    return response.json()
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }
}

export const fetchCurrentStorageSize = async (
  userId: string,
  token: string,
): Promise<number> => {
  try {
    const images = await fetchImages(userId, token)
    return images.reduce((acc, img) => acc + (img.size || 0), 0)
  } catch (error) {
    Sentry.captureException(error)
    return 0
  }
}

export const deleteImage = async (
  userId: string,
  token: string,
  fileName: string,
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/user/${userId}/gallery/${encodeURIComponent(fileName)}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'x-api-key': X_API_KEY,
        },
      },
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(
        `Delete failed: ${response.statusText}, ${JSON.stringify(errorData)}`,
      )
    }

    return response.json()
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }
}

export const updateCoverImage = async (
  userId: string,
  token: string,
  fileName: string,
) => {
  const encodedFileName = encodeURIComponent(fileName)
  const formData = new FormData()
  formData.append('userId', userId)
  formData.append('fileName', fileName)
  formData.append('cover', 'true')
  formData.append('size', 'large')

  try {
    const response = await axios.patch(
      `${API_BASE_URL}/user/${userId}/gallery/${encodedFileName}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
          'x-api-key': X_API_KEY,
        },
      },
    )

    if (response.status !== 200) {
      throw new Error(
        `Update cover failed: ${response.statusText}, ${JSON.stringify(
          response.data,
        )}`,
      )
    }

    return response.data
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }
}

export const updateImageStatus = async (
  userId: string,
  token: string,
  fileName: string,
  newStatus: number,
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/user/${userId}/gallery/${encodeURIComponent(fileName)}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-api-key': X_API_KEY,
        },
        body: JSON.stringify({ status: newStatus }),
      },
    )

    if (!response.ok) {
      throw new Error(
        `Failed to update image status: ${response.status} ${response.statusText}`,
      )
    }

    return response.json()
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }
}
